import React, { useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

export interface AdBreakdown {
  name: string;
  value: number;
  [key: string]: string | number;
}

interface AdPieChartProps {
  data?: AdBreakdown[] | null;   // ★ null/undefined OK
  periodLabel?: string;
  loading?: boolean;             // ★任意
  emptyLabel?: string;           // ★任意
}

const COLORS = ["#3b82f6", "#6366f1", "#22c55e", "#f59e0b", "#f97316"];
const PLACEHOLDER_COLOR = "#e5e7eb";

const AdPieChart: React.FC<AdPieChartProps> = ({
  data,
  periodLabel,
  loading = false,
  emptyLabel = "データがありません",
}) => {
  // ★強制的に “安全な配列＋数値” にする（string/NaN対策）
  const safeData = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];
    return arr
      .map((d) => ({
        name: String(d?.name ?? ""),
        value: Number((d as any)?.value ?? 0) || 0,
      }))
      .filter((d) => d.name.length > 0);
  }, [data]);

  const total = useMemo(() => safeData.reduce((sum, d) => sum + (Number(d.value) || 0), 0), [safeData]);

  const hasItems = safeData.length > 0;
  const hasTotal = total > 0;

  // ★total=0 でも枠が真っ白にならないように “ダミー1slice” を入れる
  const pieData = useMemo(() => {
    if (loading) return [{ name: "loading", value: 1, __placeholder: true } as any];
    if (!hasItems) return [{ name: "empty", value: 1, __placeholder: true } as any];
    if (!hasTotal) return [{ name: "zero", value: 1, __placeholder: true } as any];
    return safeData;
  }, [loading, hasItems, hasTotal, safeData]);

  return (
    <div className="w-full rounded-2xl border bg-white p-4 shadow-sm">
      {periodLabel && (
        <div className="mb-3 text-sm font-semibold text-slate-800">{periodLabel}</div>
      )}

      {/* ★狭い幅で左が潰れて “何も表示されない” を防ぐ：縦積み→横並び */}
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-0">
        {/* 円グラフ本体 */}
        <div className="w-full sm:flex-1 sm:min-w-[220px]">
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={100}
                  paddingAngle={0}
                  minAngle={3}
                  isAnimationActive={false}
                >
                  {pieData.map((d: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={d?.__placeholder ? PLACEHOLDER_COLOR : COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>

                <Tooltip
                  formatter={(value, _name, props) => {
                    const payload: any = (props as any)?.payload;

                    // placeholder は tooltip 出さない
                    if (payload?.__placeholder) return ["", ""];

                    const v = Number(value) || 0;
                    const percent = total > 0 ? ((v / total) * 100).toFixed(1) + "%" : "0%";
                    return [`${v.toLocaleString()}（${percent}）`, payload?.name ?? ""];
                  }}
                  // 空表示でも tooltip が変に出ないように
                  wrapperStyle={{ display: hasTotal ? "block" : "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* 中央メッセージ（スケルトン無し） */}
          {!hasTotal && (
            <div className="mt-2 text-center text-sm text-slate-500">
              {loading ? "読み込み中..." : emptyLabel}
            </div>
          )}
        </div>

        {/* 凡例 */}
        <div className="w-full sm:ml-4 sm:w-auto sm:min-w-[160px] sm:self-center">
          {hasItems ? (
            <div className="flex flex-col justify-center gap-1 text-xs">
              {safeData.map((item, index) => {
                const percent = total > 0 ? ((item.value / total) * 100).toFixed(1) + "%" : "0%";
                return (
                  <div key={item.name} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block h-3 w-3 rounded-sm"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-slate-700">{item.name}</span>
                    </div>
                    <span className="text-slate-500">{percent}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-xs text-slate-500">{loading ? "読み込み中..." : emptyLabel}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdPieChart;
